import { bestGuessRegion } from "./localisation-service";
import { OAuth2Token } from "../models/login-model";
import { showDialog } from "./dialog-service";
import { logEnablementMetric } from "../helpers/MetricsHelper";
const SMOKEBALL_TOKEN_STORAGE_KEY = "SmokeballToken";

export enum DialogLoginMessageTypes {
  LoginFailed = "login_failed",
  LoginCompleted = "login_completed",
}

export interface TDialogLoginFailed {
  MessageType: DialogLoginMessageTypes.LoginFailed;
  Error: string;
}

export interface TDialogLoginSuccess {
  MessageType: DialogLoginMessageTypes.LoginCompleted;
  Token: OAuth2Token;
}

export const showLogin = async () => {
  return new Promise<void>((resolve, reject) => {
    const eventReceivedHandler: Parameters<Office.Dialog["addEventHandler"]>[1] = (args) => {
      if (!("error" in args)) {
        return;
      }

      switch (args.error) {
        case 12002:
          reject(
            new Error(
              "The dialog box has been directed to a page that it cannot find or load, or the URL syntax is invalid."
            )
          );
          break;
        case 12003:
          reject(new Error("The dialog box has been directed to a URL with the HTTP protocol. HTTPS is required."));
          break;
        case 12006:
          // 12006 means that the user closed the dialog instead of waiting for it to close.
          // It is not known if the user completed the login or logout, so assume the user is
          // logged out and revert to the app's starting state. It does no harm for a user to
          // press the login button again even if the user is logged in.
          reject(new Error(""));
          break;
        default:
          reject(new Error(`Unknown error while logging in (${args.error.toString()}).`));
          break;
      }
    };

    const handleLoginFailure = (message: TDialogLoginFailed) => {
      let errorText = "";

      if (message.Error) {
        errorText = message.Error;
      }

      reject(new Error(errorText));
    };

    const handleLoginSuccess = (message: TDialogLoginSuccess) => {
      try {
        if (message.Token.accessToken === "") {
          reject(new Error("The message Token was not valid..."));
          return;
        }

        if (setTokenToStorage(message.Token)) {
          void logEnablementMetric();
          resolve();
        }
      } catch (error) {
        const errorMsg = JSON.stringify(error);

        const dialogMsg: TDialogLoginFailed = {
          Error: errorMsg,
          MessageType: DialogLoginMessageTypes.LoginFailed,
        };

        handleLoginFailure(dialogMsg);
      }
    };

    const handleDialogMessage = (loginDialog: Office.Dialog, messageData: string) => {
      if (!messageData) {
        return;
      }

      const payload = JSON.parse(messageData) as TDialogLoginFailed | TDialogLoginSuccess | undefined;

      if (payload?.MessageType) {
        switch (payload.MessageType) {
          case DialogLoginMessageTypes.LoginCompleted: {
            loginDialog.close();
            handleLoginSuccess(payload);

            return;
          }

          case DialogLoginMessageTypes.LoginFailed: {
            loginDialog.close();
            handleLoginFailure(payload);

            return;
          }

          default: {
            return;
          }
        }
      }
    };

    console.log("launching logging in dialog...");
    showDialog(`/login/login.html?region=${bestGuessRegion().code}`, {
      width: 30,
      height: 40,
      promptBeforeOpen: false,
    })
      .then((result) => {
        if (result.hasError) {
          reject(new Error(`Could not open login.`));
          console.error(`Could not open login`);
          return;
        }

        const dialogMessageHandler: Parameters<Office.Dialog["addEventHandler"]>[1] = (args) => {
          let message = "";

          if ("message" in args) {
            message = args.message;
          }

          if (!message) {
            return;
          }

          handleDialogMessage(result.dialog, message);
        };

        result.dialog.addEventHandler(Office.EventType.DialogEventReceived, eventReceivedHandler);
        result.dialog.addEventHandler(Office.EventType.DialogMessageReceived, dialogMessageHandler);
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          reject(error);
        } else if (typeof error === "string") {
          reject(new Error(error));
        } else {
          reject(new Error("Unknown error occurred."));
        }
      });
  });
};

function setTokenToStorage(token: OAuth2Token): boolean {
  if (!token.accessToken || hasTokenExpired(token)) {
    storeTokenInBrowser(null);
    return false;
  }

  const tokenStr = JSON.stringify(token);
  storeTokenInBrowser(tokenStr);

  return true;
}
function hasTokenExpired(token: OAuth2Token | undefined): boolean {
  if (!token?.expiresAt) {
    return true;
  }

  return token.expiresAt <= Date.now();
}

function storeTokenInBrowser(token: string | null, useSession?: boolean) {
  if (token) {
    const storage = useSession ? sessionStorage : localStorage;
    storage.setItem(SMOKEBALL_TOKEN_STORAGE_KEY, token);
  } else {
    localStorage.removeItem(SMOKEBALL_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(SMOKEBALL_TOKEN_STORAGE_KEY);
  }
}
